import {
  ChatMessage,
  ChatWrapupSheetPageType,
  UserAttributes,
  type WrapupFeedbackThankYouState,
  isAgentTransferMessage,
  Env,
} from '@chewy/chat-library-common';
import cn from 'classnames';
import styles from './AiConversationPanel.module.scss';
import { TransitionAnimation3D, Transition3DPhase } from './animations/TransitionAnimation';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ConversationContext} from '@chewy/chat-library-common';
import { useScrollHandler } from '@chewy/chat-library-common/hooks';
import { useMessageOffset } from '@chewy/chat-library-common/hooks/MessageOffset';
import ChatWrapupSheet from './ChatWrapupSheet';
import { messageHasWidgetType } from '@chewy/chat-library-common/tools';

import { AiConversationMessages } from './AiConversationMessages';

export interface AiConversationPanelProps {
  messages: ChatMessage[];
  phase: Transition3DPhase;
  submitQuestion: (question: string) => void;
  isBotTurn?: boolean;
  typingIndicator?: React.ReactNode
  scrollToItem?: (element: HTMLElement) => void;
  showWrapupSheet?: boolean;
  wrapupSheetActionType?: ChatWrapupSheetPageType;
  onWrapupSheetClose?: () => void;
  onSendFeedback?: (positive: boolean, comments?: string) => Promise<void>;
  onSendEmailChatTranscript?: (email?: string) => Promise<void>;
  isLastParticipantAgent?: boolean;
  customerData?: UserAttributes;
  sentiment?: 'positive' | 'negative' | null;
  isKeyboardOpen?: boolean;
  env?: Env;
  sessionId?: string;
}

const agentTransferCurve = (
  <svg
    viewBox="0 0 400 20"
    preserveAspectRatio="none"
    aria-hidden
  >
    <defs>
      <path
        id="transferCurvePath"
        d="M0 0
           H400
           A1000 1000 0 0 1 0 0
           Z"
      />
    </defs>
    <use href="#transferCurvePath" fill="var(--curve-background)" />
    <use href="#transferCurvePath" fill="var(--curve-foreground)" />
  </svg>
);

const BOTTOM_LOCK_THRESHOLD_PX = 24;

export default function AiConversationPanel({
  messages,
  phase,
  submitQuestion,
  isBotTurn,
  typingIndicator,
  scrollToItem,
  showWrapupSheet = false,
  wrapupSheetActionType = ChatWrapupSheetPageType.COMMENTS,
  onWrapupSheetClose,
  onSendFeedback,
  onSendEmailChatTranscript,
  isLastParticipantAgent = false,
  customerData,
  sentiment,
  isKeyboardOpen,
  env = 'prd',
  sessionId,
}: AiConversationPanelProps) {
  const { requestScroll } = useScrollHandler(scrollToItem);

  const mainContainerRef = useRef<HTMLDivElement>(null);
  const finalExchangeRef = useRef<HTMLDivElement>(null);

  const bottomRef = useRef<HTMLDivElement>(null);

  const { messageRef: transferMessageRef, messageOffset: transferCurveOffset, computeOffset: computeTransferCurveOffset } = useMessageOffset({
    relativeToRef: mainContainerRef,
    edge: 'bottom',
  });

  const { messageRef: curveMessageRef, messageOffset: curveOffset } = useMessageOffset({
    relativeToRef: finalExchangeRef,
  });

  /** Whether the user was already near the bottom before new content arrived. */
  const shouldStickToBottomRef = useRef(true);

  /**
   * Returns whether the scroll container is close enough to the bottom that
   * new incoming content should keep it pinned.
   */
  const isNearBottom = useCallback(() => {
    const containerElement = mainContainerRef.current;

    if (!containerElement) {
      return false;
    }

    const distanceFromBottom =
      containerElement.scrollHeight -
      containerElement.scrollTop -
      containerElement.clientHeight;

    return distanceFromBottom <= BOTTOM_LOCK_THRESHOLD_PX;
  }, []);

  // When the phase changes, compute the agent curve offset again.
  // When the conversation ends, it can become misaligned.
  useEffect(() => {
    computeTransferCurveOffset();
  }, [computeTransferCurveOffset, phase]);

  // Scroll to bottom when keyboard opens.
  // 300ms delay lets the keyboard animation finish; rAF ensures layout has settled.
  // We walk up from bottomRef to find the nearest scrollable ancestor and set
  // scrollTop directly — more reliable than scrollIntoView on mobile WebViews.
  useEffect(() => {
    if (!isKeyboardOpen) return;

    const timer = setTimeout(() => {
      requestAnimationFrame(() => {
        let el: HTMLElement | null = bottomRef.current?.parentElement ?? null;
        while (el) {
          const { overflowY } = window.getComputedStyle(el);
          if (overflowY === 'auto' || overflowY === 'scroll') {
            el.scrollTop = el.scrollHeight;
            break;
          }
          el = el.parentElement;
        }
      });
    }, 300);

    return () => clearTimeout(timer);
  }, [isKeyboardOpen]);

  const shouldScrollToMessage = useCallback(
    (message: ChatMessage) => {
      const finalMessage = messages[messages.length - 1];

      const isTextWithHeart = messageHasWidgetType(message, 'TextHeartAnimation');

      return (
        /** In order to stay pinned to bottom, we need to scroll every final message. */
        (!isTextWithHeart && shouldStickToBottomRef.current && message === finalMessage) ||
        /** Scroll to all user messages */
        message.responseType === 'user' ||
        /** Scroll to text heart animations if the chat is loaded */
        (isTextWithHeart && phase === 'complete') ||
        /** Scroll to all "Welcome Back" widget messages */
        messageHasWidgetType(message, 'WelcomeBack') ||
        /** Scroll to all agent transfer messages */
        isAgentTransferMessage(message)
      );
    },
    [messages, phase]
  );

  /**
   * Cache whether the user is still near the bottom so we can decide later
   * whether incoming content should keep the chat pinned there.
   */
  useEffect(() => {
    const containerElement = mainContainerRef.current;

    if (!containerElement) {
      return;
    }

    const updateBottomLock = () => {
      const nextShouldStickToBottom = isNearBottom();
      shouldStickToBottomRef.current = nextShouldStickToBottom;
    };

    updateBottomLock();

    containerElement.addEventListener('scroll', updateBottomLock, {
      passive: true,
    });

    return () => {
      containerElement.removeEventListener('scroll', updateBottomLock);
    };
  }, [isNearBottom]);

  const submitQuestionHandler = (question: string) => {
    if (typeof submitQuestion === 'function') {
      submitQuestion(question);
    } else {
      console.error('submitQuestion is not a function');
    }
  };

  const isEndedAgentVisualState = phase === 'ended' && isLastParticipantAgent;

  const isLayoutCompleteOrLater = ['complete', 'transferred', 'agent', 'ended'].includes(phase);

  const [wrapupFeedbackThankYou, setWrapupFeedbackThankYou] =
    useState<WrapupFeedbackThankYouState>(null);

  const clearWrapupFeedbackThankYou = useCallback(() => {
    setWrapupFeedbackThankYou(null);
  }, []);

  const handleSheetSubmit = async (value: string, type: ChatWrapupSheetPageType) => {
    if (type === ChatWrapupSheetPageType.EMAIL && onSendEmailChatTranscript) {
      try {
        await onSendEmailChatTranscript(value);
      } catch (error) {
        console.error(error)
      }
    } else if (type === ChatWrapupSheetPageType.COMMENTS && onSendFeedback) {
      try {
        await onSendFeedback(sentiment === 'positive' ? true : false, value);
      } catch (error) {
        console.error(error);
        throw error;
      }
      setWrapupFeedbackThankYou({ sentiment: sentiment ?? null });
      // Success: ChatWrapupSheet shows thank-you title until the user dismisses.
    } else {
      try {
        onWrapupSheetClose?.();
      } catch (error) {
        console.error(error);
      }
    }
  };

  const hasAgentTransferMessage = messages.some(
    message =>
      messageHasWidgetType(message, 'Transferring') || messageHasWidgetType(message, 'AgentJoined')
  );

  return (
    <ConversationContext.Provider
      value={{
        submitQuestion: submitQuestionHandler,
        phase,
        requestScroll,
        wrapupFeedbackThankYou,
        clearWrapupFeedbackThankYou,
        isBotTurn,
        env,
        sessionId,
      }}
    >
      <TransitionAnimation3D isActive={true} phase={phase} isLastParticipantAgent={isLastParticipantAgent}>
        <div
          ref={mainContainerRef}
          className={cn(styles.mainContainer, {
            [styles.mainContainer_aiBot]: !['agent', 'transferred'].includes(phase) && !isEndedAgentVisualState,
          })}
          data-main-container-phase={phase}
        >
          <section
            role="log"
            aria-live="polite"
            aria-relevant="additions text"
            style={{
              '--curve-offset': `${curveOffset}px`,
              '--transfer-curve-offset': `${transferCurveOffset}px`,
            } as React.CSSProperties}
            className={cn(styles.container, {
              [styles.container_agentTransferred]: hasAgentTransferMessage,
              [styles.container_complete]: isLayoutCompleteOrLater,
              [styles[`container_${phase}`]]: phase,
            })}
          >
            <div className={styles.agentCurveContainer} aria-hidden={true}>
              <div className={styles.agentCurveBackground} />
              {agentTransferCurve}
            </div>
            <AiConversationMessages
              messages={messages}
              shouldScrollToMessage={shouldScrollToMessage}
              messageRefs={{
                finalExchangeRef: finalExchangeRef,
                curveMessageRef: curveMessageRef,
                transferMessageRef: transferMessageRef,
              }}
              typingIndicator={typingIndicator}
            />
          </section>
          <div className={styles.bottomRef} ref={bottomRef} aria-hidden={true} />
          <ChatWrapupSheet data={{
            onCancel: () => onWrapupSheetClose?.(),
            onSubmit: handleSheetSubmit,
            actionType: wrapupSheetActionType,
            isOpen: showWrapupSheet,
            customerData: customerData,
            sentiment: sentiment,
          }} />
        </div>
      </TransitionAnimation3D>
    </ConversationContext.Provider>
  )
}
